import numeral from 'numeral';
import { Box, FormHelperText, type TextFieldProps } from '@mui/material';
import * as Ramda from 'ramda';
import CoverageCheckbox from '../../../../../agencies/quotes/PrimeX/customizeQuote/components/CoverageCheckbox';
import TickedSlider from '../../../../../agencies/quotes/PrimeX/customizeQuote/components/TickedSlider';
import { deriveOptionFormatter } from '../../utils';
import { EnhancedSelect } from '../../../../../agencies/quotes/250/EnhancedSelect';
import RetroActiveInput from '../../components/RetroActiveInput';
import type { GlobalFieldKeys } from '../../types';
import { useLocale } from '../../providers/LocaleProvider';
import {
  StyledSelect,
  StyledTextField,
} from '../../views/underwriter/components/rater-x-components/styles';
import useTypingTimeout from '../../views/underwriter/hooks/useTypingTimeout';
import type { Nullable } from '../../../../../../types/common/utility';
import { CleaveInput } from '../../../../../../components/inputs/CurrencyField';
import { stripSpecialChars } from '../../views/underwriter/components/coverages/modals/shared/common';

type CoverageCheckBoxProps = {
  children?: React.ReactNode;
  isMandatory?: boolean;
  isSelected?: boolean;
  isAvailable?: boolean;
  tooltip?: React.ReactNode;
  label?: string;
  onClick?: (isSelected: boolean) => void;
  disabled?: boolean;
  ellipsisLabel?: boolean;
};

export const CheckBox = ({
  children,
  isMandatory,
  isSelected,
  isAvailable,
  tooltip,
  label,
  onClick,
  disabled,
  ellipsisLabel,
}: CoverageCheckBoxProps) => {
  const isDisabled = !!disabled;
  const checkBoxProps = {
    description: tooltip,
    label,
    rulesAndFactors: children,
    checkboxProps: {
      ...(!isDisabled && {
        onClick: () => {
          onClick?.(!isSelected);
        },
      }),
      checked: isSelected,
    },
    disabled,
  };

  let CheckBoxElement = CoverageCheckbox.Available;

  if (isSelected && isAvailable) {
    CheckBoxElement = CoverageCheckbox.Selected;
  }

  if (!isAvailable) {
    CheckBoxElement = CoverageCheckbox.NotAvailable;
  }

  if (isMandatory) {
    CheckBoxElement = CoverageCheckbox.Mandatory;
  }

  const CheckBoxComponent = CheckBoxElement as React.ComponentType<
    typeof checkBoxProps
  >;

  return <CheckBoxComponent {...checkBoxProps} />;
};

type LimitFieldProps = {
  limit: number;
  limits: number[];
  isAvailable: boolean;
  isSelected: boolean;
  onChange: (key: GlobalFieldKeys, value: number) => void;
  /**
   * Renders the figure beside the slider. Optional, and the default is what
   * every caller had before — four personas share this field, so the slot is
   * inert unless one asks for it.
   *
   * It exists because the underwriter's draft has to mark that figure as
   * PROPOSED and hang the committed one under it, and that is the same element
   * either way. The alternative was a structural CSS selector reaching for
   * "the span after the slider" from outside, which breaks the first time this
   * JSX is touched and says nothing about why.
   */
  renderValue?: (formatted: string, value: number) => React.ReactElement;
};

/**
 * The limit slider's own compact notation — `2M`, `250K`, `2.3M`.
 *
 * Exported because anything rendering a SECOND limit beside the slider's has to
 * match it: the underwriter's draft shows the committed limit under the
 * proposed one, and the two came out as `1M` over `$2,000,000` when each
 * formatted the figure its own way. One notation, one place.
 */
export const formatCompactLimit = (value: number): string =>
  numeral(value).format('0[.]0[0]a');

export const LimitField = (props: LimitFieldProps) => {
  const locale = useLocale();
  // Not currency-threaded yet: these slider marks render compact ("2M", "250K"), a different
  // format from the retention dropdown, so converting them is a separate visual change. See MS-417.
  const marks = props.limits.map(deriveOptionFormatter(locale.language));

  return (
    <TickedSlider
      boxProps={{
        pl: 1,
        alignItems: 'center',
        gap: '1.5rem',
        width: '100%',
      }}
      initialSliderValue={props.limit}
      min={props.limits.length <= 1 ? 0 : props.limits[0]}
      max={props.limits[props.limits.length - 1]}
      marks={marks as any}
      step={null}
      disabled={
        !props.isAvailable || props.limits.length === 1 || !props.isSelected
      }
      onChange={(newValue: number | number[]) =>
        props.onChange('limit', newValue as number)
      }
    >
      {(sliderValue: number | number[]) => {
        const value = sliderValue as number;
        const formatted = formatCompactLimit(value);

        if (props.renderValue) {
          return props.renderValue(formatted, value);
        }
        return <span style={{ width: '1.5rem' }}>{formatted}</span>;
      }}
    </TickedSlider>
  );
};

type DeductibleFieldProps = {
  deductibles: number[];
  deductible: number;
  isAvailable: boolean;
  isSelected: boolean;
  onChange: (key: GlobalFieldKeys, value: number) => void;
  /** ISO 4217 code for the record these options belong to. See `deriveOptionFormatter`. */
  currency?: string | null;
};

export const DeductibleField = (props: DeductibleFieldProps) => {
  const locale = useLocale();
  if (!props.deductibles?.length) {
    return <span>-</span>;
  }

  const options = props.deductibles.map(
    deriveOptionFormatter(locale.language, undefined, props.currency)
  );

  return (
    <EnhancedSelect
      disabled={
        (props.deductibles && props.deductibles?.length <= 1) ||
        !props.isAvailable ||
        !props.isSelected
      }
      variant="naked"
      coverage={props}
      options={options}
      value={props.deductible}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        props.onChange('deductible', Number(e.target.value))
      }
    />
  );
};

type TerritorialLimitFieldProps = {
  territorialLimits: string[];
  territorialLimit: string;
  isAvailable: boolean;
  isSelected: boolean;
  onChange: (key: GlobalFieldKeys, value: string) => void;
};

export const TerritorialLimitField = (props: TerritorialLimitFieldProps) => {
  const locale = useLocale();
  if (!props.territorialLimits?.length) {
    return <span>-</span>;
  }

  const options = props.territorialLimits.map(
    deriveOptionFormatter(locale.language, 'territorialLimit')
  );

  return (
    <EnhancedSelect
      disabled={
        (props.territorialLimits && props.territorialLimits?.length <= 1) ||
        !props.isAvailable ||
        !props.isSelected
      }
      variant="naked"
      coverage={props}
      options={options}
      value={props.territorialLimit}
      renderValue={(value: string) => (
        <span
          style={{
            display: 'inline-block',
            maxWidth: '60px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value}
        </span>
      )}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        props.onChange('territorialLimit', e.target.value)
      }
    />
  );
};

type BasisOfCoverFieldProps = {
  basisOfCovers: string[];
  basisOfCover: string;
  isAvailable: boolean;
  isSelected: boolean;
  onChange: (key: GlobalFieldKeys, value: string) => void;
};

export const BasisOfCoverField = (props: BasisOfCoverFieldProps) => {
  const locale = useLocale();
  if (!props.basisOfCovers?.length) {
    return <span>-</span>;
  }

  const options = props.basisOfCovers.map(
    deriveOptionFormatter(locale.language, 'basisOfCover')
  );
  return (
    <EnhancedSelect
      disabled={
        (props.basisOfCovers && props.basisOfCovers?.length <= 1) ||
        !props.isAvailable ||
        !props.isSelected
      }
      variant="naked"
      coverage={props}
      options={options}
      renderValue={(value: string) => (
        <span
          style={{
            display: 'inline-block',
            maxWidth: '60px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value}
        </span>
      )}
      value={props.basisOfCover}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        props.onChange('basisOfCover', e.target.value)
      }
    />
  );
};

type WaitingPeriodFieldProps = {
  waitingPeriods: number[];
  waitingPeriod: number;
  isAvailable: boolean;
  isSelected: boolean;
  onChange: (key: GlobalFieldKeys, value: number) => void;
};

export const WaitingPeriodField = (props: WaitingPeriodFieldProps) => {
  const locale = useLocale();
  if (!props.waitingPeriods?.length) {
    return <span>-</span>;
  }

  const options = props.waitingPeriods.map(
    deriveOptionFormatter(locale.language, 'waitingPeriod')
  );

  return (
    <EnhancedSelect
      disabled={
        (props.waitingPeriods && props.waitingPeriods?.length <= 1) ||
        !props.isAvailable ||
        !props.isSelected
      }
      variant="naked"
      coverage={props}
      options={options}
      value={props.waitingPeriod}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        props.onChange('waitingPeriod', Number(e.target.value))
      }
    />
  );
};

type RetroActiveFieldProps = {
  retroActivePeriods: Array<number | string>;
  retroActivePeriod: RetroActivePeriod;
  isAvailable: boolean;
  isSelected: boolean;
  onChange: (key: GlobalFieldKeys, value: number | string) => void;
  isCustomDateAllowed: boolean;
  maxDate: string | undefined;
  customDateDefault: string | undefined;
};

export const RetroActiveField = (props: RetroActiveFieldProps) => {
  const locale = useLocale();
  const isDisabled =
    (props.retroActivePeriods && props.retroActivePeriods?.length <= 1) ||
    !props.isAvailable ||
    !props.isSelected;

  if (!props.retroActivePeriods?.length) {
    return <span>-</span>;
  }

  if (!props.isCustomDateAllowed) {
    const retroOptions = props.retroActivePeriods.map(
      deriveOptionFormatter(locale.language, 'retroactive')
    );

    return (
      <EnhancedSelect
        disabled={isDisabled}
        variant="naked"
        coverage={props}
        options={retroOptions}
        value={props.retroActivePeriod.period.value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          props.onChange('retroactive', Number(e.target.value))
        }
      />
    );
  }

  const _retroActivePeriods = props.retroActivePeriods;
  if (props.retroActivePeriod.date?.value) {
    if (
      !props.retroActivePeriods.includes(props.retroActivePeriod.date?.value)
    ) {
      _retroActivePeriods.push(props.retroActivePeriod.date?.value);
    }
  }

  return (
    <RetroActiveInput
      variant="coverage"
      disabled={isDisabled}
      setValue={(value: string | number) => {
        props.onChange('retroactive', value);
      }}
      options={_retroActivePeriods}
      value={
        props.retroActivePeriod?.date?.value ??
        props.retroActivePeriod.period?.value ??
        ''
      }
      maxDate={props.maxDate as string}
      defaultDate={props.customDateDefault as string}
    />
  );
};

export const LimitDropdown = (props: LimitFieldProps) => {
  const locale = useLocale();
  const _options = props.limits.map(
    deriveOptionFormatter(locale.language, 'limit')
  );

  return (
    <StyledSelect
      options={_options}
      name="limit"
      value={props.limit}
      disabled={!props.isAvailable || !props.isSelected}
      onChange={(e) => props.onChange('limit', e.target.value as number)}
      boxProps={{
        display: 'flex',
        justifyContent: 'end',
      }}
    />
  );
};

type CoverageTextFieldProps = {
  value: number;
  isAvailable: boolean;
  isSelected: boolean;
  onChange: (key: GlobalFieldKeys, value: number) => void;
  name: string;
  min: Nullable<number>;
  max: Nullable<number>;
  step: Nullable<number>;
  textFieldProps?: TextFieldProps;
};

export const CoverageTextField = (props: CoverageTextFieldProps) => {
  const validateInput = (value: number) => {
    if (!Ramda.isNil(props.min) && !Ramda.isNil(props.max)) {
      if (value < props.min || value > props.max) {
        return 'Invalid';
      }

      if (props.step && value % props.step !== 0) {
        return 'Invalid';
      }
    }
  };

  const typingTimeout = useTypingTimeout(
    (_value) => {
      props.onChange(props.name as any, Number(_value));
    },
    {
      validate: validateInput,
      defaultValue: props.value,
    }
  );

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      gap="0.25rem"
    >
      {typingTimeout.error && (
        <FormHelperText>{typingTimeout.error}</FormHelperText>
      )}
      <StyledTextField
        InputProps={{
          inputComponent: CleaveInput,
        }}
        size="small"
        value={typingTimeout.input}
        onChange={(e) =>
          typingTimeout.onChange(stripSpecialChars('', e.target.value))
        }
        disabled={
          !props.isAvailable || !props.isSelected || props.min === props.max
        }
        name="retention"
        {...props.textFieldProps}
      />
    </Box>
  );
};

export type RetroActivePeriod = Record<'date', Value> & Record<'period', Value>;

type Value = Partial<Record<'value', number>>;
